import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import { useApp } from '../store';
import { useLang, LangToggle } from '../i18n';

const ROOMS = [
  { level:1, nameZh:'初级场', nameEn:'Beginner',     carry:1000,    carryFmt:'1,000',     tagZh:'新手入门', tagEn:'Starter',  req:1000    },
  { level:2, nameZh:'中级场', nameEn:'Intermediate',  carry:10000,   carryFmt:'10,000',    tagZh:'进阶对决', tagEn:'Advanced', req:10000   },
  { level:3, nameZh:'高级场', nameEn:'Advanced',      carry:1000000, carryFmt:'1,000,000', tagZh:'精英战场', tagEn:'Elite',    req:1000000 },
];

const BLUE = '#2E7FE8';
const RED  = '#E8453C';
const GOLD = '#F5C518';

function finiteNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

export default function Lobby() {
  const { user, logout, refreshUser } = useApp();
  const { t, lang } = useLang();
  const ROOM_STATUS_LABELS = {
    WAITING: lang==='en'?'Matching':'匹配中',
    READYING: lang==='en'?'Readying':'准备中',
    COUNTDOWN: lang==='en'?'Countdown':'倒计时',
    RUNNING: lang==='en'?'In Progress':'进行中',
    SETTLING: lang==='en'?'Settling':'结算中',
  };
  const nav = useNavigate();
  const [price, setPrice]         = useState(null);
  const [change, setChange]       = useState(0);
  const [h24Change, setH24Change] = useState(null); // DexScreener 24h 真实涨幅
  const [copied, setCopied]       = useState(false);
  const [claimMsg, setMsg]        = useState('');

  const ANSEM_MINT = '9cRCn9rGT8V2imeM2BaKs13yhMEais3ruM3rPvTGpump';

  function copyContract() {
    navigator.clipboard?.writeText(ANSEM_MINT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  }
  const [roomStats, setRoomStats] = useState({});
  const [now, setNow] = useState(Date.now());

  // 每秒更新 now，驱动倒计时
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    refreshUser().catch(() => {});
  }, [refreshUser]);

  useEffect(() => {
    const update = async () => {
      try {
        const r = await API.get('/api/game/price');
        const p = Number(r.price);
        if (!isNaN(p) && p > 0) {
          setPrice(p.toFixed(4));
        }
      } catch {}
    };
    update();
    const iv = setInterval(update, 500);
    return () => clearInterval(iv);
  }, []);

  // DexScreener 拉取 24h 真实涨幅，每60秒刷新一次
  useEffect(() => {
    const fetchH24 = async () => {
      try {
        const r = await fetch(
          'https://api.dexscreener.com/latest/dex/tokens/9cRCn9rGT8V2imeM2BaKs13yhMEais3ruM3rPvTGpump',
          { signal: AbortSignal.timeout(6000) }
        );
        const d = await r.json();
        const pairs = (d.pairs || []).filter(p => p.chainId === 'solana');
        pairs.sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0));
        const p24 = pairs[0]?.priceChange?.h24;
        if (p24 !== undefined && p24 !== null) setH24Change(Number(p24));
      } catch {}
    };
    fetchH24();
    const iv = setInterval(fetchH24, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const loadRoomStats = async () => {
      try {
        const data = await API.get('/api/game/rooms/status');
        const next = {};
        for (const row of data.list || []) next[Number(row.level)] = row;
        setRoomStats(next);
      } catch {}
    };
    loadRoomStats();
    const iv = setInterval(loadRoomStats, 5000);
    return () => clearInterval(iv);
  }, []);

  async function claimCards() {
    try { const r = await API.post('/api/daily/claim-cards'); setMsg(`房卡已补充到 ${r.cards} 张`); refreshUser(); }
    catch(e) { setMsg(`${e}`); }
    setTimeout(()=>setMsg(''), 3000);
  }
  async function claimPoints() {
    try { const r = await API.post('/api/daily/claim-points'); setMsg(`积分已补充到 ${Number(r.points).toLocaleString()}`); refreshUser(); }
    catch(e) { setMsg(`${e}`); }
    setTimeout(()=>setMsg(''), 3000);
  }

  if (!user) return null;
  const pts = finiteNumber(user.points);
  const cards = finiteNumber(user.cards);
  const isUp = h24Change !== null ? h24Change >= 0 : change >= 0;
  const displayChange = h24Change !== null ? h24Change : change;

  const S = `
    .lobby-root{min-height:100vh;min-height:100dvh;
      background:linear-gradient(180deg,#0B1E4A 0%,#0D2B6B 50%,#091840 100%);
      background-attachment:fixed;position:relative;overflow-x:hidden;}
    .lobby-glow{position:fixed;top:0;left:0;right:0;height:400px;pointer-events:none;
      background:radial-gradient(ellipse at top,rgba(46,127,232,0.2) 0%,transparent 60%);
      z-index:0;}
    .lobby-inner{position:relative;z-index:1;max-width:480px;margin:0 auto;padding:0 0 80px;}
    .l-header{display:flex;align-items:center;justify-content:space-between;padding:56px 20px 20px;}
    .l-avatar{width:40px;height:40px;border-radius:50%;background:#0F2554;
      border:none;display:flex;align-items:center;
      justify-content:center;font-size:16px;font-weight:800;color:${BLUE};}
    .l-balance{padding:0 20px 24px;}
    .l-balance-label{font-size:15px;color:#8BA8D4;margin-bottom:6px;}
    .l-balance-num{font-size:42px;font-weight:900;color:#fff;letter-spacing:-1px;line-height:1;}
    .l-balance-sub{display:flex;align-items:center;gap:8px;margin-top:8px;}
    .l-actions{display:grid;grid-template-columns:repeat(auto-fit,minmax(76px,1fr));gap:10px;padding:0 20px 28px;}
    .l-action-btn{background:#0F2554;border:none;border-radius:14px;
      padding:12px 6px;min-height:64px;display:flex;flex-direction:column;align-items:center;
      justify-content:center;gap:6px;
      cursor:pointer;transition:all 0.15s;
      box-shadow:inset 0 1px 0 rgba(255,255,255,0.07);}
    .l-action-btn:hover{background:#152D6A;border-color:rgba(46,127,232,0.5);}
    .l-action-btn:active{transform:translateY(2px);}
    .l-action-btn:disabled{opacity:.45;cursor:not-allowed;transform:none;}
    .l-action-btn:disabled:hover{background:#0F2554;}
    .l-action-label{font-size:clamp(12px,3.2vw,14px);color:#ffffff;font-weight:600;
      line-height:1.25;text-align:center;word-break:keep-all;hyphens:none;}
    .l-section{padding:0 20px;font-size:18px;font-weight:700;color:#ffffff;margin-bottom:14px;letter-spacing:0.3px;}
    .l-btc-card{margin:0 20px 24px;background:#0F2554;border-radius:18px;
      border:none;padding:18px 20px;
      box-shadow:inset 0 1px 0 rgba(255,255,255,0.07);
      display:flex;align-items:center;justify-content:space-between;}
    .l-room{margin:0 20px 12px;background:#0F2554;border-radius:16px;
      border:none;padding:16px 18px;
      box-shadow:inset 0 1px 0 rgba(255,255,255,0.06);
      display:flex;align-items:center;justify-content:space-between;
      cursor:pointer;transition:all 0.15s;}
    .l-room:hover{background:#152D6A;border-color:rgba(46,127,232,0.5);transform:translateX(3px);}
    .l-room-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;
      justify-content:center;font-size:17px;font-weight:900;flex-shrink:0;}
    .l-room-enter{padding:10px 22px;border-radius:12px;font-size:15px;font-weight:700;
      border:none;cursor:pointer;transition:all 0.15s;}
    .l-nav{position:fixed;bottom:0;left:0;right:0;background:rgba(9,24,64,0.97);
      backdrop-filter:blur(20px);border-top:1px solid rgba(46,127,232,0.3);
      display:flex;justify-content:space-around;padding:12px 0 20px;z-index:200;}
    .l-nav-item{display:flex;flex-direction:column;align-items:center;gap:4px;
      color:#ffffff;font-size:15px;font-weight:600;cursor:pointer;padding:0;flex:1;
      transition:color 0.15s;}
    .l-nav-item.active{color:${GOLD};}
    .l-nav-dot{width:4px;height:4px;border-radius:50%;background:${GOLD};margin-top:2px;}
  `;

  return (
    <div className="lobby-root">
      <style>{S}</style>
      {/* 顶部蓝色光晕 */}
      <div className="lobby-glow" />

      <div className="lobby-inner">

        {/* 顶部栏 */}
        <div className="l-header">
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div>
              <div style={{fontSize:14,color:'#8BA8D4'}}>{t('welcomeBack')}</div>
              <div style={{fontSize:16,fontWeight:700,color:'#fff'}}>{user.nickname||user.email}</div>
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <LangToggle />
            <div style={{
              padding:'7px 16px',borderRadius:12,
              background: cards > 0 ? 'rgba(46,127,232,0.15)' : 'rgba(232,69,60,0.15)',
              border:'none',
            }}>
              <span style={{
                fontSize:15,fontWeight:700,fontFamily:'Share Tech Mono',
                color: '#ffffff',
              }}>{lang==='en'?`${cards} Cards`:`${cards} 张房卡`}</span>
            </div>
            <button onClick={()=>nav('/card-sale')} style={{
              padding:'7px 14px',borderRadius:12,border:'none',cursor:'pointer',
              background:'linear-gradient(180deg,#FFD700,#E6A800)',
              color:'#000',fontSize:12,fontWeight:800,
              boxShadow:'0 3px 0 #8B6200',
            }}>{t('sellCard')}</button>
            <button onClick={logout} style={{
              background:'transparent',border:'none',color:'#4A6A9A',
              fontSize:20,cursor:'pointer',lineHeight:1,
            }}>&#x2715;</button>
          </div>
        </div>

        {/* Total Balance */}
        <div className="l-balance">
          <div className="l-balance-label">{t('pointBalance')}</div>
          <div className="l-balance-num">{pts.toLocaleString()}<span style={{fontSize:20,fontWeight:500,color:'#4A6A9A',marginLeft:6}}>COIN</span></div>
        </div>

        {claimMsg && (
          <div style={{margin:'0 20px 16px',padding:'11px 16px',borderRadius:12,
            background:'rgba(46,127,232,0.1)',border:'none',
            color:BLUE,fontSize:15,fontWeight:500}}>{claimMsg}</div>
        )}

        {/* 快捷操作 */}
        <div className="l-actions">
          {[
            {key:'claimCard',   action: claimCards,             disabled: user.canClaimCard === false},
            {key:'claimPoints', action: claimPoints,            disabled: user.canClaimPoints === false},
            {key:'leaderboard', action: ()=>nav('/leaderboard')},
            {key:'friendBattle',action: ()=>nav('/friend')},
            {key:'ledger',      action: ()=>nav('/ledger')},
            ...(user.is_admin ? [{key:'adminLabel', action:()=>nav('/admin')}] : []),
          ].map(({key,action,disabled})=>(
            <button key={key} className="l-action-btn" onClick={action} disabled={disabled}>
              <div className="l-action-label">{t(key)}</div>
            </button>
          ))}
        </div>

        {/* ANSEM 实时价格 */}
        <div className="l-section">{t('marketSection')}</div>
        <div className="l-btc-card" style={{flexDirection:'column',gap:14}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',width:'100%'}}>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:44,height:44,borderRadius:10,background:'#fff',
                display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,overflow:'hidden'}}>
                <img src="/ansem-logo.webp" alt="ANSEM" style={{width:44,height:44,objectFit:'cover',borderRadius:10}}/>
              </div>
              <div>
                <div style={{fontSize:16,fontWeight:700,color:'#ffffff'}}>$ANSEM / USDT</div>
                {/* 合约地址可复制 */}
                <div
                  onClick={copyContract}
                  style={{
                    display:'flex',alignItems:'center',gap:4,marginTop:3,cursor:'pointer',
                  }}
                >
                  <span style={{fontSize:12,color:'#8BA8D4',fontFamily:'Share Tech Mono',letterSpacing:0.3}}>
                    {ANSEM_MINT.slice(0,6)}…{ANSEM_MINT.slice(-4)}
                  </span>
                  <span style={{fontSize:12,color:copied?'#2ECC71':'#4A6A9A'}}>
                    {copied ? '✓' : '⎘'}
                  </span>
                </div>
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontFamily:'Share Tech Mono',fontSize:22,fontWeight:700,color:'#ffffff'}}>
                {price ? `$${parseFloat(price).toFixed(4)}` : <span style={{color:'#4A6A9A'}}>--</span>}
              </div>
              <div style={{
                display:'inline-block',marginTop:4,padding:'2px 10px',borderRadius:8,
                fontSize:13,fontWeight:700,
                background:isUp?'rgba(46,204,113,0.12)':'rgba(232,69,60,0.12)',
                color:isUp?'#2ECC71':RED,
              }}>
                {h24Change !== null
                  ? `${isUp?'+':''}${displayChange.toFixed(2)}% 24h`
                  : t('loading')}
              </div>
            </div>
          </div>
        </div>

        {/* 战场选择 */}
        <div className="l-section" style={{marginTop:4}}>{t('chooseBattlefield')}</div>
        {ROOMS.map((room) => {
          const canEnter = pts >= room.req && cards >= 1;
          const stat = roomStats[room.level] || {};
          const playerCount = finiteNumber(stat.playerCount);
          const roomName = lang==='en' ? room.nameEn : room.nameZh;
          const roomTag  = lang==='en' ? room.tagEn  : room.tagZh;

          // 倒计时逻辑
          const GAME_DURATION = 60; // 秒
          let statusText;
          if (stat.status === 'RUNNING' && stat.startedAt) {
            const elapsed = Math.floor((now - stat.startedAt) / 1000);
            const remain  = Math.max(0, GAME_DURATION - elapsed);
            statusText = lang==='en'
              ? (remain > 0 ? `In Progress · ${remain}s left` : 'Settling...')
              : (remain > 0 ? `游戏中 · 剩余 ${remain}s` : '结算中...');
          } else {
            statusText = stat.status ? ROOM_STATUS_LABELS[stat.status] || stat.status : t('roomStatusIdle');
          }

          const playersText = lang==='en'
            ? `${playerCount.toLocaleString()} players · ${statusText}`
            : `当前 ${playerCount.toLocaleString()} 名玩家 · ${statusText}`;
          return (
            <div key={room.level} className="l-room"
              style={{opacity:canEnter?1:0.45}}
              onClick={()=>canEnter&&nav(`/game/${room.level}`)}>
              <div style={{display:'flex',alignItems:'center',gap:14}}>
                <div>
                  <div style={{fontSize:17,fontWeight:700,color:'#fff'}}>{roomName}</div>
                  <div style={{fontSize:14,color:'#8BA8D4',marginTop:3}}>
                    {roomTag} · {t('carryLabel')} {room.carryFmt} COIN
                  </div>
                  <div style={{fontSize:14,color:'#F5C518',marginTop:5,fontWeight:700}}>
                    {playersText}
                  </div>
                </div>
              </div>
              <button className="l-room-enter"
                style={{
                  background: canEnter
                    ? 'linear-gradient(180deg, #FFD700 0%, #E6A800 100%)'
                    : 'rgba(15,37,84,0.8)',
                  color: canEnter ? '#000' : '#4A6A9A',
                  fontWeight: canEnter ? 800 : 600,
                  boxShadow: canEnter ? '0 4px 0 #8B6200, 0 6px 12px rgba(0,0,0,0.3)' : 'none',
                  border:'none',
                  pointerEvents:'none',
                }}
                disabled={!canEnter}>
                {!canEnter ? (cards<1 ? t('noCard') : t('noPoints')) : t('startMatch')}
              </button>
            </div>
          );
        })}

        {/* 玩法说明 */}
        <div style={{margin:'20px 20px 0',background:'#0F2554',borderRadius:16,
          border:'none',padding:'16px 18px',
          boxShadow:'inset 0 1px 0 rgba(255,255,255,0.06)'}}>
          <div style={{fontSize:15,fontWeight:600,color:'#8BA8D4',marginBottom:14,letterSpacing:0.5}}>{t('gameRulesTitle')}</div>
          {[
            [t('rule1Title'), t('rule1Desc')],
            [t('rule2Title'), t('rule2Desc')],
            [t('rule3Title'), t('rule3Desc')],
          ].map(([title,desc])=>(
            <div key={title} style={{display:'flex',gap:10,marginBottom:12,alignItems:'flex-start'}}>
              <div style={{width:7,height:7,borderRadius:'50%',background:BLUE,marginTop:5,flexShrink:0}}/>
              <div>
                <span style={{fontSize:15,fontWeight:700,color:'#fff'}}>{title} </span>
                <span style={{fontSize:15,color:'#8BA8D4',lineHeight:1.6}}>{desc}</span>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* 底部导航 */}
      <div className="l-nav">
        {[
          {key:'navHome',     active:true,  action:()=>nav('/lobby')},
          {key:'navTasks',    active:false, action:()=>nav('/tasks')},
          {key:'navInvite',   active:false, action:()=>nav('/invite')},
          {key:'navWithdraw', active:false, action:()=>nav('/withdraw')},
        ].map(({key,active,action})=>(
          <div key={key} className={`l-nav-item${active?' active':''}`} onClick={action}>
            <span style={{fontSize:15,fontWeight:active?700:600}}>{t(key)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
